// src/components/web/CuponesModal.js
import React, { useEffect, useMemo, useState } from "react";

// Base de API/archivos: en prod usa mismo origen si no hay env
const API_BASE = process.env.REACT_APP_API_BASE;
const resolveUploadBase = () => {
  const env = (API_BASE || "").trim();
  if (env) return env.replace(/\/+$/, "");
  return window.location.origin.replace(/\/+$/, "");
};
const UPLOAD_BASE = resolveUploadBase();
const FALLBACK_IMG = "/nodisponible.png"; // desde /public

const ALLOWED_HOSTS = new Set([
  "localhost:5001",
  "promequi.com.gt:443",
  "www.promequi.com.gt:443",
]);
// Nota: UPLOAD_BASE ya resuelto arriba para evitar 'undefined/uploads' en producción

/* ------------------------------ helpers UI ------------------------------ */
function formatDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch {}
  return String(v).slice(0, 10);
}
function initial(str = "") {
  const s = String(str).trim();
  return s ? s[0].toUpperCase() : "C";
}
function textOnBg(hex = "#F97316") {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#FFFFFF";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const toLin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  return L > 0.6 ? "#111827" : "#FFFFFF";
}

/** Normaliza la ruta (igual espíritu que en el catálogo) */
function buildImgSrc(imagen) {
  let src = String(imagen || "").trim();
  if (!src) return FALLBACK_IMG;

  src = src.replace(/\\/g, "/");

  if (/^https?:\/\//i.test(src)) {
    try {
      const u = new URL(src);
      const hp = `${u.hostname}:${u.port || (u.protocol === "https:" ? "443" : "80")}`;
      if (ALLOWED_HOSTS.has(hp)) return src;
      const baseName = src.split("/").pop();
      return baseName ? `${UPLOAD_BASE}/uploads/${baseName}` : FALLBACK_IMG;
    } catch {
      return FALLBACK_IMG;
    }
  }

  if (src.startsWith("/uploads/")) return `${UPLOAD_BASE}${src}`;
  if (src.startsWith("/")) return `${UPLOAD_BASE}${src}`;
  return `${UPLOAD_BASE}/uploads/${src}`;
}

/** Img sin parpadeo: si falla, queda en fallback hasta que cambie el src */
function ImgWithFallback({ src, alt, className = "", ...rest }) {
  const normalized = useMemo(() => buildImgSrc(src), [src]);
  const [errored, setErrored] = useState(false);

  useEffect(() => { setErrored(false); }, [normalized]);

  return (
    <img
      src={errored ? FALLBACK_IMG : normalized}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      {...rest}
    />
  );
}

/**
 * Ahora el modal NO hace fetch. Recibe `items` por props.
 * Además, no se desmonta: usamos `style={{display: open?'block':'none'}}`.
 */
export default function CuponesModal({ open, onClose, onSelect, items = [] }) {
  const list = Array.isArray(items) ? items : [];

  return (
    <div
      className="fixed inset-0 z-[96]"
      style={{ display: open ? "block" : "none" }}
      aria-hidden={!open}
    >
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div
        className="relative mx-auto mt-10 w-[96%] max-w-6xl bg-white rounded-2xl shadow-2xl p-6 sm:p-8"
        onKeyDownCapture={(e) => e.stopPropagation()}
      >
        {/* encabezado */}
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-extrabold text-slate-900">Cupones disponibles</h3>
          <button
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        {/* contenido */}
        {list.length === 0 ? (
          <p className="mt-6 text-slate-500">No hay cupones activos en este momento.</p>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((c) => {
              const bg = c.color_hex || "#F97316";
              const ink = textOnBg(bg);
              const imgSrc = c.imagen;

              return (
                <article
                  key={c.id ?? c.codigo ?? Math.random()}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect?.(c)}
                  onKeyDown={(e) => e.key === "Enter" && onSelect?.(c)}
                  className="rounded-[28px] overflow-hidden shadow-md ring-1 ring-black/5 cursor-pointer transition-transform hover:scale-[1.015] focus:scale-[1.015] outline-none"
                  style={{ backgroundColor: bg, color: ink }}
                >
                  <div className="p-5 relative">
                    {/* overlay sutil de legibilidad */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0.18) 100%)",
                      }}
                    />
                    <div className="relative z-[1]">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-white/15 ring-1 ring-white/25 overflow-hidden flex items-center justify-center">
                            {imgSrc ? (
                              <ImgWithFallback
                                src={imgSrc}
                                alt={c.producto_nombre_snapshot || c.nombre}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-bold">
                                {initial(c.producto_nombre_snapshot || c.nombre)}
                              </span>
                            )}
                          </div>
                          <span className="text-sm/5 opacity-90">Promoción activa</span>
                        </div>
                        {!!c.codigo && (
                          <span
                            className="text-xs font-semibold px-2 py-1 rounded-lg uppercase"
                            style={{
                              backgroundColor: "rgba(255,255,255,.15)",
                              border: "1px solid rgba(255,255,255,.25)",
                            }}
                          >
                            {c.codigo}
                          </span>
                        )}
                      </div>

                      {/* Título */}
                      <h4 className="mt-3 text-2xl font-extrabold tracking-tight">
                        {c.nombre}
                      </h4>

                      {/* Producto */}
                      {c.producto_nombre_snapshot && (
                        <p className="mt-1 text-sm/6 opacity-95">
                          Producto:{" "}
                          <span className="font-semibold">
                            {c.producto_nombre_snapshot}
                          </span>
                        </p>
                      )}

                      {/* divisoria */}
                      <div
                        className="my-3"
                        style={{ height: 1, backgroundColor: "rgba(255,255,255,.2)" }}
                      />

                      {/* Descuento / Vence */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs/5 opacity-85">Descuento</div>
                          <div className="text-xl font-bold">
                            -{Number(c.porcentaje_descuento || 0)}%
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs/5 opacity-85">Vence</div>
                          <div className="text-xl font-bold">
                            {formatDate(c.fecha_fin)}
                          </div>
                        </div>
                      </div>

                      {/* Imagen principal del producto */}
                      <div className="mt-5 flex justify-center">
                        <div className="bg-white rounded-2xl p-3 w-[220px] h-[220px] flex items-center justify-center overflow-hidden">
                          {imgSrc ? (
                            <ImgWithFallback
                              src={imgSrc}
                              alt={c.producto_nombre_snapshot || c.nombre}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full rounded-xl bg-slate-200" />
                          )}
                        </div>
                      </div>

                      {/* Descripción */}
                      {c.descripcion && (
                        <p className="mt-4 text-sm/6 opacity-95 text-center">
                          {c.descripcion}
                        </p>
                      )}

                      {/* Pie */}
                      <div className="mt-3 text-center text-sm font-semibold tracking-widest">
                        PROMEQUI
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
